import {
  OrderSheet,
  OrderSheetStatus,
} from "@/features/order-sheets/order-sheet.type";
import { LineSheet } from "@/features/line-sheet-sets/line-sheet-set.types";
import { camelCase, snakeCase } from "lodash";
import { toLower } from "@/utils/case";
import { Deserializable, Serializable } from "@/features/ui/app.type";
import { string } from "yup";
import { Deserializer } from "node:v8";
import { AttachmentInvoiceTypes } from "@/shared/types";

export interface Company {
  id: number;
  name: string;
  orgName?: string | null;
  type?: CompanyType;
  logoUrl?: string | null;
  // 브랜드의 유사 표기(라인시트 파싱용으로 관리하는 목록). 검색·자동완성에서 이름과 함께 본다 —
  // 'EMPORIO ARMANI' 나 '프라다' 처럼 시트/사용자가 쓰는 표기로도 브랜드를 찾게 하려는 것.
  // 브랜드가 아닌 회사는 빈 배열.
  similarWords?: string[];
  features?: string[];
  // 패스픽 멤버십 — null이면 해당 회사는 패스픽 권한이 없다 (바이어 외 회사 등)
  pathpickMembership?: PathPickMembership | null;
  // 패스픽 멤버십 신청 이력 여부 — 해지·반려 포함 한 번이라도 신청했으면 true.
  // 첫 달 프로모션(사상 첫 결제) 대상 판정에 쓴다. 응답에 없으면 false 취급.
  hasPathpickMembershipApplication?: boolean;
  // 무료체험(FREE)을 이미 쓴 바이어인가 — 바이어당 1회라 true면 재신청 불가.
  hasUsedPathpickFreeTrial?: boolean;
  // 멤버십 상태 — GNB 노출·멤버십 화면 분기용. 서버가 계산해 내려준다.
  pathpickMembershipStatus?: PathPickMembershipStatus;
}

// NONE(가입 후 결제 전) / TRIAL(무료체험 중) / TRIAL_EXPIRED(체험 만료) /
// SUBSCRIBED(유료 플랜 이용 중) / SUBSCRIPTION_EXPIRED(유료 이력 있으나 현재 미배정)
export type PathPickMembershipStatus =
  | "NONE"
  | "TRIAL"
  | "TRIAL_EXPIRED"
  | "SUBSCRIBED"
  | "SUBSCRIPTION_EXPIRED";

export type MembershipType =
  | "FREE" // 무료체험 플랜 — 상태 TRIAL과 함께 온다
  | "BASIC"
  | "PRO"
  | "BUSINESS"
  | "PARTNER"
  | "ENTERPRISE";

export interface PathPickMembership {
  code: MembershipType;
  name: MembershipType;
  // 배정 만료 시각(무료체험). null이면 무기한(유료 플랜). ISO 문자열.
  expiresAt?: string | null;
  // 매월 결제일(1~31). null이면 미설정. 멤버십 관리 카드 표시용.
  renewalDay?: number | null;
  monthlyQuota: number | null;
  allowKreamDownload: boolean;
  allowNaverDownload: boolean;
  allowNaverSync: boolean;
  allowHotdeal: boolean;
  allowStockOrder: boolean;
  stockOrderMonthlyLimit: number;
}

export interface LightCompany {
  id: number;
  name: string;
}

export interface BrandsType {
  id: number;
  name: string;
}

export type CompanyType =
  | "BOUTIQUE"
  | "BUYER"
  | "AGENCY"
  | "BRAND"
  | "CROSS_TRADER";

export const CompanyTypes: CompanyType[] = [
  "BOUTIQUE",
  "BUYER",
  "AGENCY",
  "BRAND",
  "CROSS_TRADER",
];

export function isCompanyType(candidate: any): candidate is CompanyType {
  return CompanyTypes.includes(candidate);
}

export interface Brand extends Omit<Company, "type"> {
  type: "BRAND";
}

export interface Boutique extends Omit<Company, "type"> {
  type: "BOUTIQUE";
}

export interface Buyer extends Omit<Company, "type"> {
  type: "BUYER";
}

export interface Agency extends Omit<Company, "type"> {
  type: "AGENCY";
}

export interface CrossTrader extends Omit<Company, "type"> {
  type: "CROSS_TRADER";
}

export function getCompanySuffix(type: CompanyType): string {
  switch (type) {
    case "BOUTIQUE":
      return "BT";
    case "BUYER":
      return "BY";
    case "AGENCY":
      return "A";
    case "BRAND":
      return "BR";
    case "CROSS_TRADER":
      return "T";
  }
}

export interface ProductPrice {
  amount: number;
  currency: string;
}

export interface QuantityWithOption {
  productOptionId?: number;
  name: string;
  quantity: number;
  isNew?: boolean;
}

export interface StoredObject {
  id: number;
  name: string;
  url: string | null;
  createdAt: Date | null;
}

export interface TypedFileObject {
  id: number;
  name: string;
  type: AttachmentInvoiceTypes;
  created_at: string | null;
}

export interface TransientStoredObject {
  id: number;
  file: File;
}

export function isStoredObject(
  item: File | Pick<StoredObject, "id">
): item is StoredObject {
  if (item && typeof item === "object") {
    const object = item as StoredObject;
    return object.id !== undefined;
  }
  return false;
}

export function isTransientStoredObject(
  item: File | Pick<StoredObject, "id"> | TransientStoredObject
): item is TransientStoredObject {
  if (typeof item === "object") {
    const object = item as TransientStoredObject;
    return object.file !== undefined;
  }
  return false;
}

export function isFile(item: File | Pick<StoredObject, "id">): item is File {
  return item instanceof File;
}

export function getFileNameWithoutExtension(
  raw: File | string | Pick<StoredObject, "id" | "name">
) {
  let fullName;

  if (raw instanceof File) {
    fullName = raw.name;
  } else if (typeof raw === "object") {
    fullName = raw.name;
  } else {
    fullName = raw;
  }

  const lastDot = fullName.lastIndexOf(".");
  if (lastDot !== -1) {
    return fullName.substring(0, lastDot);
  }
  return fullName;
}

export function getFileExtension(fileOrFileName: File | string) {
  const name =
    fileOrFileName instanceof File ? fileOrFileName.name : fileOrFileName;
  const lastDot = name.lastIndexOf(".");
  if (lastDot !== -1) {
    return name.substring(lastDot + 1);
  }
  return "";
}

export function isExcel(
  item: File | Pick<StoredObject, "id" | "name">
): boolean {
  return ["xls", "xlsx", "csv", "xlsm", "xlsb"].some((ext) =>
    toLower(item.name).endsWith(ext)
  );
}

export function isPDF(item: File | Pick<StoredObject, "id" | "name">): boolean {
  return ["pdf"].some((ext) => toLower(item.name).endsWith(ext));
}

export interface PreSignedStoredObject {
  id: number;
  name: string;
  url: string;
}

export interface PageNumberBasedListRequest {
  pageNumber: number;
  pageSize: number;
}

export interface PageNumberBasedListResponse {
  totalCount: number;
  statistics?: Record<OrderSheetStatus | "totalCount", number>;
}

export interface CursorBasedListRequest {
  pageSize: number;
  pageToken?: string;
}

export interface CursorBasedListResponse {
  nextPageToken: string | null;
}

export interface IdentityParameter {
  by: CompanyType;
  companyId: number;
}

export interface ViewLineSheet extends LineSheet {
  orderSheet?: OrderSheet;
}

export interface TransientFile<Type = any> {
  id: string;
  binary: File;
  type?: Type;
  number1?: number | "";
}

export type SortOrder = "ascend" | "descend";

export class OrderByItem extends Deserializable implements Serializable {
  readonly field: string;
  readonly sortOrder?: SortOrder;

  constructor(field: string, sortOrder: SortOrder = "ascend") {
    super();
    this.field = field;
    this.sortOrder = sortOrder;
  }

  serialize(): string {
    let prefix = "";
    if (this.sortOrder === "descend") {
      prefix = "-";
    }
    return `${prefix}${snakeCase(this.field)}`;
  }

  static deserialize(str: string) {
    let field = str;
    let sortOrder: SortOrder = "ascend";
    if (str.startsWith("-")) {
      field = str.substring(1);
      sortOrder = "descend";
    }
    return new OrderByItem(camelCase(field), sortOrder);
  }
}

export function isOrderByItem(obj: any): obj is OrderByItem {
  const candidate = obj as OrderByItem;
  return (
    typeof candidate.field !== "undefined" && isSortOrder(candidate.sortOrder)
  );
}

export function isOrderBy(obj: any): obj is OrderByItem[] {
  if (Array.isArray(obj)) {
    return obj.every((i) => isOrderByItem(i));
  }
  return false;
}

function isSortOrder(obj: any): obj is SortOrder {
  if (obj === undefined) {
    return true;
  } else if (typeof obj === "string") {
    return obj === "ascend" || obj === "descend";
  }
  return false;
}

export function serializeOrderBy(orderBy: OrderByItem): string;
export function serializeOrderBy(orderBy: OrderByItem[]): string[];
export function serializeOrderBy(orderBy: OrderByItem | OrderByItem[]): any {
  const func = (obj: OrderByItem) => {
    let prefix = "";
    if (obj.sortOrder === "descend") {
      prefix = "-";
    }
    return `${prefix}${snakeCase(obj.field)}`;
  };

  if (Array.isArray(orderBy)) {
    return orderBy.map((i) => func(i));
  } else {
    return func(orderBy);
  }
}

export interface LightUser {
  id: number;
  name: string;
  email: string;
}

export interface Price {
  value: number;
  currency: "EUR" | "USD" | "HKD" | "KRW" | "EA";
}

export function isPrice(value: any): value is Price {
  return value && value.currency && typeof value.value === "number";
}

// 모르는 통화 코드는 코드를 그대로 돌려준다(심볼이 비어 금액만 보이지 않게).
export function getCurrencySymbol(currency: Price["currency"] | "%" | string) {
  if (currency === "EUR") {
    return "€";
  } else if (currency === "USD") {
    return "$";
  } else if (currency === "HKD") {
    return "HK$";
  } else if (currency === "KRW") {
    return "₩";
  } else if (currency === "EA") {
    return "EA";
  } else if (currency === "%") {
    return "%";
  }
  return currency;
}

export type FileType = "pdf" | "excel";

export function getMIMEType(fileType: FileType): string | string[] {
  switch (fileType) {
    case "pdf":
      return "application/pdf";
    case "excel":
      return [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
  }
}

export function getAccept(fileType?: FileType[]): string | undefined {
  if (fileType) {
    return fileType.map((type) => getMIMEType(type)).join(",");
  }
}
